import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LineChart, RefreshCw, Loader2, Eye, ExternalLink, Info, Crosshair, Mail, Download, ShieldCheck, Zap, Pencil, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';
import CompetitorPositionBadge, { POSITIONS, fmtEur, fmtDelta, fmtDate, hostOf } from './CompetitorPositionBadge';
import CompetitorProductDrawer from './CompetitorProductDrawer';
import AlignPriceDialog from './AlignPriceDialog';
import BulkAlignDialog from './BulkAlignDialog';
import CompetitorConnectionSettings from './CompetitorConnectionSettings';
import CompetitorTyreSources from './CompetitorTyreSources';

const FILTERS = [
  { id: '', label: 'Tous' },
  { id: 'more_expensive', label: POSITIONS.more_expensive.label },
  { id: 'cheaper', label: POSITIONS.cheaper.label },
  { id: 'similar', label: POSITIONS.similar.label },
  { id: 'not_fetched', label: POSITIONS.not_fetched.label },
];
const SORTS = [
  { id: 'gap', label: 'Écart de prix (plus cher en premier)' },
  { id: 'last_fetch', label: 'Dernier relevé' },
  { id: 'evolution', label: 'Évolution du prix' },
];

const CompetitorCell = ({ c, testId }) => {
  if (!c) return <td className="px-4 py-3 text-xs text-gray-300">—</td>;
  return (
    <td className="px-4 py-3 text-sm" data-testid={testId}>
      {c.status === 'ok' && c.price != null ? (
        <>
          <span className="font-medium text-gray-900">{fmtEur(c.price)}</span>
          {c.old_price ? <span className="ml-1 text-xs text-gray-400 line-through">{fmtEur(c.old_price)}</span> : null}
          <span className="block text-[11px] text-gray-500">{hostOf(c.url)}{c.availability ? ` · ${c.availability}` : ''}{c.shipping != null ? ` · port ${c.shipping === 0 ? 'offert' : fmtEur(c.shipping)}` : ''}{c.via === 'manual' ? <span className="ml-1 px-1 rounded bg-amber-50 text-amber-700" title="Prix saisi à la main (aucun crédit consommé)">saisi à la main</span> : c.via && c.via !== 'direct' ? <span className="ml-1 px-1 rounded bg-blue-50 text-blue-700" title="Récupéré via la connexion sortante (IP française)">via {c.via}</span> : null}</span>
        </>
      ) : (
        <>
          <span className="text-xs font-medium text-gray-500">Prix non récupéré</span>
          <span className="block text-[11px] text-gray-400" title={c.error || ''}>{hostOf(c.url)}{c.error ? ` · ${c.error}` : ''}</span>
        </>
      )}
    </td>
  );
};

const AutoRunToggle = ({ settings, onSaved }) => {
  const [saving, setSaving] = useState(false);
  if (!settings) return null;
  const enabled = settings.auto_run_enabled !== false;
  const toggle = async () => {
    setSaving(true);
    try {
      const r = await competitorsAPI.setConnection({ auto_run_enabled: !enabled });
      onSaved(r);
      toast.success(r.auto_run_enabled ? 'Relevé mensuel automatique activé' : 'Relevé mensuel automatique désactivé — seuls les relevés manuels seront effectués');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };
  return (
    <button type="button" onClick={toggle} disabled={saving} data-testid="competitors-auto-run-toggle"
      title={enabled ? 'Cliquer pour désactiver le relevé automatique du 1er du mois (économise vos crédits de scraping)' : 'Cliquer pour réactiver le relevé automatique du 1er du mois'}
      className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 transition-colors ${enabled ? 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
      {saving ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
      Relevé mensuel auto : <strong data-testid="competitors-auto-run-value">{enabled ? 'activé' : 'désactivé'}</strong>
    </button>
  );
};

const MarginFloorSetting = ({ settings, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  if (!settings) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await competitorsAPI.setSettings(parseFloat(value.replace(',', '.')));
      toast.success(`Marge plancher : ${r.min_margin_pct} %`);
      onSaved(r); setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-white border rounded-full px-2.5 py-1" data-testid="competitors-margin-floor">
      <ShieldCheck size={13} className="text-green-600" /> Marge HT plancher (auto) :
      {editing ? (
        <>
          <Input type="number" step="1" min="0" max="90" value={value} onChange={(e) => setValue(e.target.value)} data-testid="competitors-margin-floor-input" className="w-16 h-6 text-xs px-1" autoFocus />%
          <button type="button" onClick={save} disabled={saving} className="text-green-700 font-medium hover:underline" data-testid="competitors-margin-floor-save">{saving ? '…' : 'OK'}</button>
          <button type="button" onClick={() => setEditing(false)} className="text-gray-400 hover:underline">Annuler</button>
        </>
      ) : (
        <>
          <strong data-testid="competitors-margin-floor-value">{settings.min_margin_pct} %</strong>
          <button type="button" onClick={() => { setValue(String(settings.min_margin_pct)); setEditing(true); }} className="text-gray-400 hover:text-gray-700" title="Modifier" data-testid="competitors-margin-floor-edit"><Pencil size={12} /></button>
          {settings.auto_align_count > 0 && <span className="ml-1 inline-flex items-center gap-1 text-orange-700" data-testid="competitors-auto-count"><Zap size={12} /> {settings.auto_align_count} produit{settings.auto_align_count > 1 ? 's' : ''} en auto</span>}
        </>
      )}
    </span>
  );
};

const AdminCompetitorPricesPanel = ({ onEditProduct, onPriceChanged }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('gap');
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [align, setAlign] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const pollRef = useRef(null);
  const onAligned = () => { load(); onPriceChanged?.(); };

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([competitorsAPI.getOverview(filter, sort), competitorsAPI.getSettings()])
      .then(([d, s]) => { setData(d); setRun(d.last_run); setSettings(s); })
      .catch(() => toast.error('Chargement impossible')).finally(() => setLoading(false));
  }, [filter, sort]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const name = await competitorsAPI.exportCsv(filter);
      toast.success(`Export téléchargé : ${name}`);
    } catch {
      toast.error('Export impossible');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (run?.status !== 'running') { clearInterval(pollRef.current); return undefined; }
    pollRef.current = setInterval(async () => {
      try {
        const r = await competitorsAPI.latestRun();
        setRun(r);
        if (r?.status !== 'running') { clearInterval(pollRef.current); load(); toast.success(`Relevé terminé : ${r.ok} prix récupéré${r.ok > 1 ? 's' : ''}${r.failed ? `, ${r.failed} non récupéré${r.failed > 1 ? 's' : ''}` : ''}`); }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [run?.status, run?.id, load]);

  const startRun = async (productId = null) => {
    setStarting(true);
    try {
      const r = await competitorsAPI.run(productId);
      setRun(r);
      toast.info(productId ? 'Relevé du produit lancé' : `Relevé lancé pour ${r.total} produit${r.total > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de lancer le relevé');
    } finally {
      setStarting(false);
    }
  };

  const running = run?.status === 'running';
  const items = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div data-testid="admin-competitors-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><LineChart className="text-orange-500" /> Veille tarifaire</h1>
          <p className="text-sm text-gray-500 mt-1">Comparez vos prix à ceux des concurrents. {settings?.auto_run_enabled === false ? 'Relevé automatique désactivé : utilisez « Relever maintenant » ou le bouton ↻ d\'un produit.' : 'Relevé automatique le 1er de chaque mois à 06h00, historique conservé.'}</p>
          {run && (
            <p className="text-xs text-gray-500 mt-1" data-testid="competitors-last-run">
              {running ? <span className="inline-flex items-center gap-1 text-blue-700"><Loader2 size={12} className="animate-spin" /> Relevé en cours : {run.done} / {run.total} produit{run.total > 1 ? 's' : ''}</span>
                : <>Dernier relevé : {fmtDate(run.finished_at || run.started_at)} · {run.trigger === 'auto' ? 'automatique' : 'manuel'} · {run.ok} prix récupéré{run.ok > 1 ? 's' : ''}{run.failed ? `, ${run.failed} non récupéré${run.failed > 1 ? 's' : ''}` : ''}
                  {run.alerts > 0 && <span className="ml-2 inline-flex items-center gap-1 text-red-600" data-testid="competitors-alert-sent"><Mail size={12} /> alerte e-mail envoyée ({run.alerts} produit{run.alerts > 1 ? 's' : ''} plus cher{run.alerts > 1 ? 's' : ''})</span>}
                  {run.auto_aligned > 0 && <span className="ml-2 inline-flex items-center gap-1 text-green-700" data-testid="competitors-auto-aligned"><Zap size={12} /> {run.auto_aligned} prix aligné{run.auto_aligned > 1 ? 's' : ''} automatiquement</span>}
                  {run.auto_blocked > 0 && <span className="ml-2 inline-flex items-center gap-1 text-amber-700" data-testid="competitors-auto-blocked"><ShieldCheck size={12} /> {run.auto_blocked} bloqué{run.auto_blocked > 1 ? 's' : ''} par la marge plancher</span>}</>}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <MarginFloorSetting settings={settings} onSaved={(s) => setSettings(prev => ({ ...prev, ...s }))} />
            <AutoRunToggle settings={settings} onSaved={(s) => setSettings(prev => ({ ...prev, ...s }))} />
            <p className="text-xs text-gray-400">Après chaque relevé, un e-mail d'alerte est envoyé à l'admin si un concurrent passe sous notre prix.</p>
          </div>
          <CompetitorConnectionSettings key={settings ? 'ready' : 'wait'} settings={settings} onSaved={(s) => setSettings(prev => ({ ...prev, ...s }))}
            defaultTestUrl={items.find(r => r.competitor_urls?.[0])?.competitor_urls[0] || ''} />
          <CompetitorTyreSources onAssigned={() => load()} />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="competitors-export-csv" onClick={exportCsv} disabled={exporting || !data?.total} title={filter ? 'Exporte uniquement le filtre actif' : 'Exporte tous les produits suivis'}>
            {exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />} Exporter CSV
          </Button>
          {counts.more_expensive > 0 && (
            <Button variant="outline" data-testid="competitors-bulk-align" onClick={() => setBulkOpen(true)} disabled={running} className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/5">
              <Crosshair size={16} className="mr-2" /> Aligner tous les produits plus chers ({counts.more_expensive})
            </Button>
          )}
          <Button data-testid="competitors-run-all" onClick={() => startRun()} disabled={running || starting || !data?.total} className="bg-orange-500 hover:bg-orange-600">
            {running || starting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />} Relever maintenant
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map(f => {
          const n = f.id ? counts[f.id] || 0 : data?.total || 0;
          const p = POSITIONS[f.id];
          return (
            <button key={f.id} type="button" data-testid={`competitors-filter-${f.id || 'all'}`} onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filter === f.id ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
              {p && <span className={`w-2 h-2 rounded-full ${p.dot}`} />} {f.label} <span className={`ml-0.5 ${filter === f.id ? 'text-white/80' : 'text-gray-400'}`}>({n})</span>
            </button>
          );
        })}
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="competitors-sort" className="ml-auto text-sm border rounded-md px-2 py-1.5 bg-white">
          {SORTS.map(s => <option key={s.id} value={s.id}>Trier : {s.label}</option>)}
        </select>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : !data?.total ? (
        <div className="bg-white rounded-lg shadow-sm p-10 text-center" data-testid="competitors-empty">
          <Info size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucun produit suivi pour le moment</p>
          <p className="text-sm text-gray-500 mt-1">Ouvrez une fiche produit (onglet Produits → modifier) et renseignez jusqu'à 3 URLs dans la section « Prix concurrents ».</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full min-w-[1100px]" data-testid="competitors-table">
            <thead className="bg-gray-50">
              <tr>
                {['Produit', 'Notre prix', 'Concurrent 1', 'Concurrent 2', 'Concurrent 3', 'Moins cher', 'Écart', 'Évolution', 'Position', 'Dernier relevé', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-500">Aucun produit dans cette catégorie</td></tr>}
              {items.map(row => {
                const snap = row.latest;
                const rep = snap?.report;
                const cheapest = rep?.cheapest_slot ? snap.competitors.find(c => c.slot === rep.cheapest_slot) : null;
                const gap = cheapest ? rep.diffs.find(d => d.slot === cheapest.slot) : null;
                const evo = rep?.evolution?.competitors?.filter(c => c.delta_pct != null && c.delta_eur !== 0) || [];
                const bySlot = (s) => snap?.competitors.find(c => c.slot === s) || (row.competitor_urls[s - 1] ? { url: row.competitor_urls[s - 1], status: 'pending' } : null);
                return (
                  <tr key={row.id} className="hover:bg-gray-50" data-testid={`competitors-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={row.image} alt="" className="w-10 h-10 object-contain bg-gray-50 rounded" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[220px]">{row.name}</p>
                          <p className="text-xs text-gray-500">{row.brand} · {row.ref}{row.auto_align && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded px-1" data-testid={`competitors-auto-badge-${row.id}`}><Zap size={9} /> Auto</span>}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900" data-testid={`competitors-our-price-${row.id}`}>{fmtEur(row.price)}</td>
                    <CompetitorCell c={bySlot(1)} testId={`competitors-c1-${row.id}`} />
                    <CompetitorCell c={bySlot(2)} testId={`competitors-c2-${row.id}`} />
                    <CompetitorCell c={bySlot(3)} testId={`competitors-c3-${row.id}`} />
                    <td className="px-4 py-3 text-sm" data-testid={`competitors-cheapest-${row.id}`}>{cheapest ? <><span className="font-medium">{fmtEur(cheapest.price)}</span><span className="block text-[11px] text-gray-500">C{cheapest.slot} · {hostOf(cheapest.url)}</span></> : '—'}</td>
                    <td className={`px-4 py-3 text-sm font-medium ${gap ? (gap.diff_eur > 0 ? 'text-red-600' : gap.diff_eur < 0 ? 'text-green-700' : 'text-gray-700') : 'text-gray-400'}`} data-testid={`competitors-gap-${row.id}`}>{gap ? fmtDelta(gap.diff_eur, gap.diff_pct) : '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600" data-testid={`competitors-evolution-${row.id}`}>
                      {evo.length ? evo.map(c => <span key={c.slot} className={`block ${c.delta_eur > 0 ? 'text-red-600' : c.delta_eur < 0 ? 'text-green-700' : ''}`}>C{c.slot} {fmtDelta(c.delta_eur, c.delta_pct)}</span>) : <span className="text-gray-400">{rep?.evolution?.previous_month ? 'Stable' : 'Premier relevé'}</span>}
                    </td>
                    <td className="px-4 py-3"><CompetitorPositionBadge position={row.position} short testId={`competitors-position-${row.id}`} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap" data-testid={`competitors-date-${row.id}`}>{fmtDate(snap?.fetched_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {row.position === 'more_expensive' && (
                          <button type="button" data-testid={`competitors-align-${row.id}`} onClick={() => setAlign({ id: row.id, name: row.name })} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#1e3a5f] text-white text-xs font-medium hover:bg-[#2d4a6f]" title="Placer notre prix juste sous le concurrent le moins cher"><Crosshair size={12} /> Aligner</button>
                        )}
                        <button type="button" data-testid={`competitors-details-${row.id}`} onClick={() => setOpenId(row.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Historique & rapport"><Eye size={16} /></button>
                        <button type="button" data-testid={`competitors-run-${row.id}`} onClick={() => startRun(row.id)} disabled={running || starting} className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-40" title="Relever maintenant ce produit"><RefreshCw size={16} /></button>
                        <a href={`/produit/${row.slug || row.id}`} target="_blank" rel="noreferrer" className="p-2 text-gray-400 hover:text-gray-700" title="Voir la fiche"><ExternalLink size={14} /></a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && (
        <CompetitorProductDrawer
          productId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          onRun={() => startRun(openId)}
          running={running}
          onEditProduct={onEditProduct}
          onAlign={(name) => setAlign({ id: openId, name })}
          alignKey={align ? 0 : 1}
          minMarginPct={settings?.min_margin_pct}
        />
      )}
      {align && <AlignPriceDialog productId={align.id} productName={align.name} onClose={() => setAlign(null)} onAligned={onAligned} />}
      {bulkOpen && <BulkAlignDialog onClose={() => setBulkOpen(false)} onAligned={onAligned} />}
    </div>
  );
};

export default AdminCompetitorPricesPanel;
